import { ReactNode, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Spacer, TwoColGrid } from "../../../components/Layout/Layout";
import { ObjectDetailsModel } from "../../../core/api/objects/types";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";
import { userDateFormat } from "../../../ui-lib/utils/userDateFormat";
import useUser from "../../../core/user/useUser";
import useLanguage from "../../../core/i18n/useLanguage";
import useTranslations from "../../../core/i18n/useTranslations";
import CollapsibleBlock from "../../../ui-lib/components/CollapsibleBlock/CollapsibleBlock";
import DatePicker from "../../../ui-lib/components/DateAndTime/DatePicker";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import Divider from "../../../ui-lib/components/Divider/Divider";
import useSharedObject from "../SharedObjectContext/useSharedObject";
import Autocomplete from "../../../ui-lib/components/Autocomplete/Autocomplete";

import styles from "./ObjectGeneral.module.css";

const DesktopReferralWrapper = ({ children }: { children: ReactNode }) => {
  const t = useTranslations();
  return (
    <div className={styles.referralWrapper}>
      <Spacer size={32} />
      <Divider />
      <Spacer size={32} />
      <CollapsibleBlock withScroll title={t("Objects:referral_info_title")}>
        {children}
      </CollapsibleBlock>
    </div>
  );
};

const MobileReferralWrapper = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);

const ReferralInfoContent = () => {
  const t = useTranslations();
  const { objectFields } = useSharedObject();
  const { currentLanguage } = useLanguage();
  const { config } = useUser();
  const { control, watch } = useFormContext<ObjectDetailsModel>();
  const selectedUrgencyOfReferral = watch("urgencyOfReferral");
  const dateFormat = useMemo(
    () =>
      config?.dateFormat ? userDateFormat(config.dateFormat) : "DD-MM-YYYY",
    []
  );
  const urgencyOfReferralOptions: IItemProp[] = useMemo(() => {
    return [
      {
        id: "1",
        name: t("Objects:priority_1"),
        isSelected: selectedUrgencyOfReferral === "1",
      },
      {
        id: "2",
        name: t("Objects:priority_2"),
        isSelected: selectedUrgencyOfReferral === "2",
      },
      {
        id: "3",
        name: t("Objects:priority_3"),
        isSelected: selectedUrgencyOfReferral === "3",
      },
      {
        id: "4",
        name: t("Objects:priority_4"),
        isSelected: selectedUrgencyOfReferral === "4",
      },
    ];
  }, [selectedUrgencyOfReferral, currentLanguage]);
  return (
    <>
      <Spacer size={32} />
      <h2>{t("Objects:referral_info_title")}</h2>
      <p>{t("Objects:referral_info_description")}</p>
      <Spacer size={16} />
      <TwoColGrid>
        <Controller
          name="dateOfReferral"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:dateOfReferral_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
        {objectFields?.urgencyOfReferral ? (
          <Autocomplete
            fieldData={objectFields?.urgencyOfReferral}
            name="urgencyOfReferral"
            defaultLabel={t("Objects:urgencyOfReferral_label")}
            defaultPlaceholder={t("Objects:urgencyOfReferral_placeholder")}
          />
        ) : (
          <Controller
            name="urgencyOfReferral"
            control={control}
            render={({ field }) => (
              <Dropdown
                withClearItem
                title={t("Objects:urgencyOfReferral_label")}
                placeholder={t("Objects:urgencyOfReferral_placeholder")}
                selectedItem={urgencyOfReferralOptions.find(
                  (i) => i.isSelected
                )}
                items={urgencyOfReferralOptions}
                onSelectItem={(o) => field.onChange(o.id === -1 ? null : o.id)}
              />
            )}
          />
        )}
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Controller
          name="installationDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:installationDate_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
        <Controller
          name="reviewUserBefore"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:reviewUserBefore_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
      </TwoColGrid>
    </>
  );
};

export const ReferralInfo = ({ isDesktop }: { isDesktop: boolean }) => {
  return (
    <>
      {isDesktop ? (
        <DesktopReferralWrapper>
          <ReferralInfoContent />
        </DesktopReferralWrapper>
      ) : (
        <MobileReferralWrapper>
          <ReferralInfoContent />
        </MobileReferralWrapper>
      )}
    </>
  );
};
